import { BaseModal } from './BaseModal';
import { SpriteLab } from './SpriteLab';
import { ColorPicker } from './ColorPicker';

export class AnimationConfigModal extends BaseModal {
    private app: SpriteLab;

    constructor(app: SpriteLab) {
        super();
        this.app = app;
        this.buildDOM();
    }

    protected buildDOM(): void {
        this.backdrop.style.background = 'transparent';
        this.backdrop.style.backdropFilter = 'none';

        this.card.style.width = '100%';
        this.card.style.maxWidth = '420px';
        this.card.style.padding = '24px';

        const closeBtn = document.createElement('button');
        closeBtn.className = 'modal-close-x';
        closeBtn.onclick = () => this.destroy();
        closeBtn.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`;
        this.card.appendChild(closeBtn);

        const title = document.createElement('h2');
        title.className = 'sl-modal-title';
        title.innerText = this.app.t('tool_anim_config') || 'Animation Configuration';
        title.style.paddingRight = '32px'; // Prevent overlapping with close button
        this.card.appendChild(title);

        const formLayout = document.createElement('div');
        formLayout.style.display = 'flex';
        formLayout.style.flexDirection = 'column';
        formLayout.style.gap = '20px';
        formLayout.style.marginTop = '16px';
        formLayout.style.width = '100%';

        // Background Color
        const bgContainer = document.createElement('div');
        bgContainer.style.display = 'flex';
        bgContainer.style.flexDirection = 'column';
        bgContainer.style.gap = '8px';
        
        const bgLabel = document.createElement('label');
        bgLabel.className = 'sl-layout-52';
        bgLabel.innerText = this.app.t('label_background_color'); 

        const bgButton = document.createElement('button');
        bgButton.type = 'button';
        bgButton.id = 'sl-bg-color';
        bgButton.title = 'Change preview background color';
        
        // Sync initial color
        const previewWrap = document.getElementById('sl-preview-wrap');
        const initialColor = previewWrap ? previewWrap.style.backgroundColor : '#00000000';
        bgButton.style.backgroundColor = initialColor;
        
        bgButton.style.width = '100%';
        bgButton.style.height = '36px';
        bgButton.style.border = '2px solid rgba(255, 255, 255, 0.2)';
        bgButton.style.borderRadius = '6px';
        bgButton.style.cursor = 'pointer';
        bgButton.style.transition = 'border-color 0.2s ease';
        
        bgButton.addEventListener('mouseenter', () => bgButton.style.borderColor = 'rgba(255,255,255,0.6)');
        bgButton.addEventListener('mouseleave', () => bgButton.style.borderColor = 'rgba(255,255,255,0.2)');

        // Create inline color picker wrapper
        const pickerWrapper = document.createElement('div');
        pickerWrapper.style.display = 'none';
        pickerWrapper.style.marginTop = '12px';
        pickerWrapper.classList.add('inline-color-picker');
        pickerWrapper.style.alignSelf = 'center';
        pickerWrapper.style.width = '100%';
        
        const inlineColorPicker = new ColorPicker(pickerWrapper, this.app.t.bind(this.app));
        inlineColorPicker.onEyeDropperFallback = async () => {
            // Use custom canvas picker
            const originalColorPicker = this.app.colorPicker;
            this.app.colorPicker = inlineColorPicker;
            this.backdrop.style.display = 'none';

            const originalClose = inlineColorPicker.close.bind(inlineColorPicker);
            inlineColorPicker.close = () => {
                originalClose();
                this.app.colorPicker = originalColorPicker;
                this.backdrop.style.display = 'flex';
                inlineColorPicker.close = originalClose;
            };

            this.app.editorManager.switchTool('picker');
        };

        const pickerEl = inlineColorPicker.getElement();
        if (pickerEl) {
            pickerEl.style.position = 'static';
            pickerEl.style.boxShadow = 'none';
            pickerEl.style.border = '1px solid rgba(255,255,255,0.1)';
            pickerEl.style.padding = '8px';
            pickerEl.style.width = '100%';
            pickerEl.style.boxSizing = 'border-box';
            pickerEl.style.background = 'rgba(0,0,0,0.2)';
            pickerEl.style.display = 'block';

            // Hide the header since it's inline
            const header = pickerEl.querySelector('.sl-cp-header') as HTMLElement;
            if (header) {
                header.style.display = 'none';
            }
        }

        let isExpanded = false;

        const togglePicker = () => {
            if (isExpanded) {
                pickerWrapper.style.display = 'none';
                bgButton.style.display = 'block';
                isExpanded = false;
                this.card.style.marginTop = '0'; // Restore margin
                inlineColorPicker.close();
            } else {
                pickerWrapper.style.display = 'block';
                bgButton.style.display = 'none';
                isExpanded = true;
                this.card.style.marginTop = '-10.5vh'; // Move to top to fit color picker
                
                const rgbToHex = (rgb: string) => {
                    const match = rgb.match(/\d+/g);
                    if (!match || match.length < 3) return rgb;
                    return "#" + (1 << 24 | parseInt(match[0]) << 16 | parseInt(match[1]) << 8 | parseInt(match[2])).toString(16).slice(1).toUpperCase();
                };
                
                const initialHex = rgbToHex(bgButton.style.backgroundColor);
                
                inlineColorPicker.open(bgButton, 0, 0, initialHex, (color) => {
                    bgButton.style.backgroundColor = color;
                    
                    const hiddenText = document.getElementById('sl-bg-color-text') as HTMLInputElement;
                    if (hiddenText) hiddenText.value = color;

                    const previewWrap = document.getElementById('sl-preview-wrap');
                    if (previewWrap) previewWrap.style.backgroundColor = color;
                }, () => {
                    // This handles clicks outside the picker inside the modal, collapsing it
                    pickerWrapper.style.display = 'none';
                    bgButton.style.display = 'block';
                    isExpanded = false;
                    this.card.style.marginTop = '0'; // Restore margin
                });
            }
        };

        // Close inline picker when clicking outside
        document.addEventListener('mousedown', (e) => {
            if (isExpanded) {
                const target = e.target as HTMLElement;
                if (!pickerWrapper.contains(target) && target !== bgButton && !bgButton.contains(target)) {
                    togglePicker();
                }
            }
        });

        // Collapse when a palette tile or canvas is clicked
        pickerWrapper.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            if (target.closest('.sl-cp-palette-slot') || 
                target.closest('.sl-cp-canvas') || 
                target.closest('.sl-cp-canvas-container')) {
                setTimeout(() => {
                    if (isExpanded) togglePicker();
                }, 10);
            }
        }, true);

        // Collapse when releasing a slider (hue/alpha)
        pickerWrapper.addEventListener('change', (e) => {
            const target = e.target as HTMLElement;
            if (target.tagName.toLowerCase() === 'input' && (target as HTMLInputElement).type === 'range') {
                setTimeout(() => {
                    if (isExpanded) togglePicker();
                }, 10);
            }
        }, true);

        // Collapse when pressing Enter in any text input (Hex/RGB)
        pickerWrapper.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                setTimeout(() => {
                    if (isExpanded) togglePicker();
                }, 10);
            }
        }, true);

        bgButton.addEventListener('click', (e) => {
            e.stopPropagation();
            togglePicker();
        });

        bgContainer.appendChild(bgLabel);
        bgContainer.appendChild(bgButton);
        bgContainer.appendChild(pickerWrapper);
        formLayout.appendChild(bgContainer);


        // Row for Animation Mode and Interval
        const rowContainer = document.createElement('div');
        rowContainer.style.display = 'flex';
        rowContainer.style.gap = '12px';
        rowContainer.style.alignItems = 'flex-start';

        // Animation Mode
        const animContainer = document.createElement('div');
        animContainer.style.display = 'flex';
        animContainer.style.flexDirection = 'column';
        animContainer.style.gap = '8px';
        animContainer.style.flex = '1';
        
        const animLabel = document.createElement('label');
        animLabel.className = 'sl-layout-52';
        animLabel.innerText = this.app.t('label_animation');

        const animSelect = document.createElement('select');
        animSelect.id = 'sl-meta-animation';
        animSelect.className = 'sl-config-select';
        animSelect.setAttribute('aria-label', 'Animation playback mode');
        animSelect.style.width = '100%';
        animSelect.style.padding = '8px';
        animSelect.style.borderRadius = '4px';
        animSelect.style.background = 'rgba(0,0,0,0.2)';
        animSelect.style.color = '#fff';
        animSelect.style.border = '1px solid rgba(255,255,255,0.1)';
        
        const seqOption = document.createElement('option');
        seqOption.value = 'sequence';
        seqOption.text = this.app.t('val_sequence');
        
        const bounceOption = document.createElement('option');
        bounceOption.value = 'bounce';
        bounceOption.text = this.app.t('val_bounce');
        
        const randomOption = document.createElement('option');
        randomOption.value = 'random';
        randomOption.text = this.app.t('val_random');
        
        animSelect.appendChild(seqOption);
        animSelect.appendChild(bounceOption);
        animSelect.appendChild(randomOption);

        // Set current animation mode from app history
        const key = `${this.app.projectId}:${this.app.itemId}:${this.app.currentSheet}`;
        const sheetData = this.app.sessionSheets.get(key);
        if (sheetData) {
            animSelect.value = sheetData.animation;
        } else {
            seqOption.selected = true;
        }
        
        animSelect.addEventListener('change', () => {
            this.app.updateAnimation(animSelect.value as 'sequence' | 'bounce' | 'random');
            this.app.checkDirty();
            this.app.updateButtonStates();
        });

        animContainer.appendChild(animLabel);
        animContainer.appendChild(animSelect);
        rowContainer.appendChild(animContainer);

        // Interval
        const intContainer = document.createElement('div');
        intContainer.style.display = 'flex';
        intContainer.style.flexDirection = 'column';
        intContainer.style.gap = '8px';
        intContainer.style.flex = '1';
        
        const intLabel = document.createElement('label');
        intLabel.className = 'sl-layout-52';
        intLabel.innerText = this.app.t('label_interval');
        
        const intDisplay = document.createElement('div');
        intDisplay.id = 'sl-interval-container';
        intDisplay.className = 'sl-layout-54';
        
        const intVal = document.createElement('span');
        intVal.id = 'sl-interval-val';
        intVal.innerText = '0.20 s';
        
        const intInput = document.createElement('input');
        intInput.type = 'number';
        intInput.id = 'sl-interval-input';
        intInput.min = '0.01';
        intInput.max = '2.5';
        intInput.step = '0.01';
        intInput.value = '0.20';
        intInput.className = 'sl-layout-31';
        
        intDisplay.appendChild(intVal);
        intDisplay.appendChild(intInput);
        
        const intRow = document.createElement('div');
        intRow.style.display = 'flex';
        intRow.style.alignItems = 'center';
        intRow.style.justifyContent = 'space-between';
        intRow.appendChild(intLabel);
        intRow.appendChild(intDisplay);
        intContainer.appendChild(intRow);
        
        const intSlider = document.createElement('input');
        intSlider.type = 'range';
        intSlider.id = 'sl-interval-slider';
        intSlider.min = '0.01';
        intSlider.max = '2.5';
        intSlider.step = '0.01';
        intSlider.value = '0.20';
        intSlider.className = 'sl-layout-32';
        intSlider.setAttribute('aria-label', 'Animation interval slider');

        // Set current interval
        if (sheetData) {
            intInput.value = sheetData.interval.toFixed(2);
            intSlider.value = sheetData.interval.toFixed(2);
            intVal.innerText = `${sheetData.interval.toFixed(2)} s`;
        }

        const updateInterval = (val: number) => {
            let clamped = Math.max(0.01, Math.min(2.5, val));
            intInput.value = clamped.toFixed(2);
            intSlider.value = clamped.toFixed(2);
            intVal.innerText = `${clamped.toFixed(2)} s`;
            this.app.updateInterval(clamped);
            this.app.updateSliderFill(intSlider);
            this.app.checkDirty();
            this.app.updateButtonStates();
        };

        // Initialize slider fill
        this.app.updateSliderFill(intSlider);

        intDisplay.style.cursor = 'pointer';
        
        intDisplay.addEventListener('click', (e: MouseEvent) => {
            if (e.target === intInput) return;
            intVal.style.display = 'none';
            intInput.style.display = 'block';
            setTimeout(() => {
                intInput.focus();
                intInput.select();
            }, 10);
        });

        const finishIntervalEditing = () => {
            const val = parseFloat(intInput.value);
            if (!isNaN(val)) updateInterval(val);
            intInput.style.display = 'none';
            intVal.style.display = 'block';
        };

        intInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') finishIntervalEditing();
        });

        intInput.addEventListener('blur', finishIntervalEditing);

        intSlider.addEventListener('input', (e) => updateInterval(parseFloat((e.target as HTMLInputElement).value)));

        intContainer.appendChild(intSlider);
        rowContainer.appendChild(intContainer);

        formLayout.appendChild(rowContainer);

        this.card.appendChild(formLayout);
    }
}
